import useTranslate from '../../useTranslate';
import { CreatePhoneUserAtLocationDto } from '../../../store/locationsSlice';
import React from 'react';
import { Form, Formik, FormikProps } from 'formik';
import ValidatorFormGroup from '../../main/validator-form-group';
import { Button, FormControl, FormGroup } from 'react-bootstrap';
import { FormikErrors } from 'formik/dist/types';
import { PhoneInput } from 'react-international-phone';
import { Link } from 'react-router';
import { PbGenericRequest } from '../../../store/pbRequestSlice';
import { RequestStatus } from '../../../store/network/managedRequest';

interface Props {
    locationId: number;
    onAdd: (creationData: CreatePhoneUserAtLocationDto) => void;
    onCancel: () => void;
    request: PbGenericRequest;
}

function LocationPhoneUserAddForm(props: Readonly<Props>) {
    const strings = useTranslate('LocationPhoneUserAddForm');
    const defaultCountry = 'CH';

    const initialValues: CreatePhoneUserAtLocationDto = {
        phone: '',
        email: '',
        terms_and_conditions_accepted: false,
    };

    return (
        <>
            <h2>{strings.title}</h2>
            <p className={'mt-2 mb-lg-4'}>{strings.description}</p>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validate={validateCreatePhoneUser}
            >
                {renderForm}
            </Formik>
        </>
    );

    function renderForm(
        formikProps: FormikProps<CreatePhoneUserAtLocationDto>
    ) {
        const shouldShowError = (field: keyof CreatePhoneUserAtLocationDto) =>
            Boolean((formikProps.touched[field] || formikProps.submitCount > 0) && formikProps.errors[field]);

        return (
            <Form onSubmit={formikProps.handleSubmit}>
                <ValidatorFormGroup
                    label={strings.fieldPhone}
                    helpLabel={strings.hintPhone}
                    hideErrorLabel={!shouldShowError('phone')}
                    errorLabel={strings.errorPhone}
                >
                    <PhoneInput
                        onChange={(value) => {
                            formikProps.setFieldTouched('phone', true, false);
                            formikProps.setFieldValue('phone', value);
                        }}
                        value={formikProps.values.phone}
                        defaultCountry={defaultCountry.toLowerCase()}
                        inputProps={{ className: 'form-control' }}
                        inputClassName={'form-control'}
                    />
                </ValidatorFormGroup>
                <ValidatorFormGroup
                    label={strings.fieldEMail}
                    hideErrorLabel={!shouldShowError('email')}
                    errorLabel={strings.errorEMail}
                >
                    <FormControl
                        type={'text'}
                        onChange={(event) => {
                            formikProps.setFieldTouched('email', true, false);
                            formikProps.handleChange(event);
                        }}
                        name={'email'}
                        value={formikProps.values.email}
                    />
                </ValidatorFormGroup>

                <FormGroup className={shouldShowError('terms_and_conditions_accepted') ? 'error' : ''}>
                    <div className="checkbox checkbox-primary checkbox-custom">
                        <input
                            type="checkbox"
                            id="terms_and_conditions_accepted"
                            className="styled"
                            checked={
                                formikProps.values.terms_and_conditions_accepted
                            }
                            onChange={(event) => {
                                formikProps.setFieldTouched(
                                    'terms_and_conditions_accepted',
                                    true,
                                    false
                                );
                                formikProps.handleChange(event);
                            }}
                            name={'terms_and_conditions_accepted'}
                        />
                        <label htmlFor="terms_and_conditions_accepted">
                            {strings.termsAndConditions0}
                            <Link
                                className="text-link"
                                to={strings.termsAndConditionsLink}
                                target={'_blank'}
                            >
                                {strings.termsAndConditions1}
                            </Link>
                            {strings.termsAndConditions2}
                            <Link
                                className="text-link"
                                to={strings.privacyPolicyLink}
                                target={'_blank'}
                            >
                                {strings.termsAndConditions3}
                            </Link>
                            {strings.termsAndConditions4}
                        </label>
                    </div>
                    {shouldShowError('terms_and_conditions_accepted') && (
                        <div className="alert alert-danger">
                            {strings.errorTermsAndConditions}
                        </div>
                    )}
                </FormGroup>

                {maybeRenderRequestState()}

                <div className={'mb-2'}>
                    <Button
                        className={'btn btn-primary'}
                        type="submit"
                        data-testid={'location-phone-user-add-form-submit'}
                    >
                        {strings.buttonAdd}
                    </Button>
                    <Button
                        className={'btn btn-secondary ml-2'}
                        type="button"
                        data-testid={'location-phone-user-add-form-cancel'}
                        onClick={props.onCancel}
                    >
                        {strings.buttonCancel}
                    </Button>
                </div>
            </Form>
        );
    }

    function maybeRenderRequestState() {
        if (props.request.status === RequestStatus.FAILURE) {
            return props.request.errors.map((it) => (
                <div className="alert-danger mb-2" key={`err-${it}`}>
                    {errorTagToText(it)}
                </div>
            ));
        }
        return null;
    }

    function errorTagToText(errorTag: string) {
        if (
            errorTag === 'EMAIL_INVALID' ||
            errorTag === 'USER_EMAIL_ALREADY_EXISTS' ||
            errorTag === 'USER_ALREADY_CREATED'
        ) {
            return strings.errorEMailAlreadyUsed;
        }
        if (
            errorTag === 'USER_PHONE_NUMBER_ALREADY_EXISTS' ||
            errorTag === 'PHONE_INVALID'
        ) {
            return strings.errorPhoneAlreadyUsed;
        }
        return strings.errorGeneric;
    }

    function handleSubmit(creationData: CreatePhoneUserAtLocationDto) {
        props.onAdd(creationData);
    }
}

const emailFormatRegex = /^[^\s@]+@[^.\s@]+\.[^\s@]+$/;
const phoneFormatRegex = /^\+(\d[ /-]*){7,}$/;

function validateCreatePhoneUser(
    values: CreatePhoneUserAtLocationDto
): FormikErrors<CreatePhoneUserAtLocationDto> {
    const errors: FormikErrors<CreatePhoneUserAtLocationDto> = {};
    if (!values.phone) {
        errors.phone = 'MISSING';
    } else if (!phoneFormatRegex.test(values.phone.trim())) {
        errors.phone = 'INVALID';
    }
    if (!values.email) {
        errors.email = 'MISSING';
    } else if (!emailFormatRegex.test(values.email.trim())) {
        errors.email = 'INVALID';
    }
    if (!values.terms_and_conditions_accepted) {
        errors.terms_and_conditions_accepted = 'MISSING';
    }
    return errors;
}

export default LocationPhoneUserAddForm;
